import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Star, 
  MapPin, 
  Building2,
  Search,
  Filter,
  X,
  ImagePlus,
  Loader2
} from 'lucide-react';

const AdminProperties = () => {
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBuilder, setFilterBuilder] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [builders, setBuilders] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    price: '',
    location: '',
    latitude: '',
    longitude: '',
    property_type: 'Apartment',
    area: '',
    area_unit: 'sq ft',
    description: '',
    images: '',
    highlights: '',
    status: 'Available',
    builder: '',
    featured: false,
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [properties, searchTerm, filterBuilder, filterStatus]);

  const fetchProperties = async () => {
    try {
      const response = await api.get('/properties');
      setProperties(response.data);
      
      // Extract unique builders
      const uniqueBuilders = [...new Set(response.data.map(p => p.builder).filter(Boolean))];
      setBuilders(uniqueBuilders);
    } catch (error) {
      toast.error('Failed to fetch properties');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...properties];

    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.builder.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterBuilder !== 'all') {
      filtered = filtered.filter((p) => p.builder === filterBuilder);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter((p) => p.status === filterStatus);
    }

    setFilteredProperties(filtered);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterBuilder('all');
    setFilterStatus('all');
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      price: '',
      location: '',
      latitude: '',
      longitude: '',
      property_type: 'Apartment',
      area: '',
      area_unit: 'sq ft',
      description: '',
      images: '',
      highlights: '',
      status: 'Available',
      builder: '',
      featured: false,
    });
    setEditingProperty(null);
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      title: property.title,
      price: property.price.toString(),
      location: property.location,
      latitude: property.latitude?.toString() || '',
      longitude: property.longitude?.toString() || '',
      property_type: property.property_type,
      area: property.area.toString(),
      area_unit: property.area_unit,
      description: property.description,
      images: property.images.join('\n'),
      highlights: property.highlights.join('\n'),
      status: property.status,
      builder: property.builder,
      featured: property.featured,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const propertyData = {
      title: formData.title,
      price: parseFloat(formData.price),
      location: formData.location,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      property_type: formData.property_type,
      area: parseFloat(formData.area),
      area_unit: formData.area_unit,
      description: formData.description,
      images: formData.images.split('\n').filter((url) => url.trim()),
      highlights: formData.highlights.split('\n').filter((h) => h.trim()),
      status: formData.status,
      builder: formData.builder,
      featured: formData.featured,
    };

    try {
      if (editingProperty) {
        await api.put(`/properties/${editingProperty.id}`, propertyData);
        toast.success('Property updated successfully');
      } else {
        await api.post('/properties', propertyData);
        toast.success('Property created successfully');
      }

      fetchProperties();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'Failed to save property';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;

    try {
      await api.delete(`/properties/${id}`);
      toast.success('Property deleted successfully');
      fetchProperties();
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  const toggleFeatured = async (property) => {
    try {
      const propertyData = {
        ...property,
        featured: !property.featured,
      };
      delete propertyData.id;
      delete propertyData.created_at;
      
      await api.put(`/properties/${property.id}`, propertyData);
      toast.success(property.featured ? 'Removed from featured' : 'Added to featured');
      fetchProperties();
    } catch (error) {
      toast.error('Failed to update property');
    }
  };

  const formatPrice = (price) => {
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(2)} Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(2)} L`;
    }
    return `₹${price.toLocaleString()}`;
  };

  return (
    <div className="space-y-6" data-testid="admin-properties">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold text-white mb-2">Property Management</h1>
          <p className="text-white/60">
            {filteredProperties.length} of {properties.length} properties
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}
          data-testid="add-property-button"
          className="btn-primary"
        >
          <Plus className="mr-2" size={18} />
          Add Property
        </Button>
      </div>

      {/* Filters */}
      <div className="glass-effect border border-dark-lighter rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
              <Input
                type="text"
                data-testid="search-properties"
                placeholder="Search by title, location, or builder..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-dark border-dark-lighter text-white pl-10"
              />
            </div>
          </div>

          <div>
            <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">Builder</Label>
            <Select value={filterBuilder} onValueChange={(v) => setFilterBuilder(v)}>
              <SelectTrigger className="bg-dark border-dark-lighter text-white">
                <SelectValue placeholder="All Builders" />
              </SelectTrigger>
              <SelectContent className="bg-dark-light border-dark-lighter">
                <SelectItem value="all" className="text-white">All Builders</SelectItem>
                {builders.map((builder) => (
                  <SelectItem key={builder} value={builder} className="text-white">
                    {builder}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">Status</Label>
            <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v)}>
              <SelectTrigger className="bg-dark border-dark-lighter text-white">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="bg-dark-light border-dark-lighter">
                <SelectItem value="all" className="text-white">All Status</SelectItem>
                <SelectItem value="Available" className="text-white">Available</SelectItem>
                <SelectItem value="Sold" className="text-white">Sold</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {(searchTerm || filterBuilder !== 'all' || filterStatus !== 'all') && (
          <div className="mt-4 flex justify-end">
            <Button
              onClick={resetFilters}
              variant="ghost"
              className="text-white/60 hover:text-white"
            >
              <X size={16} className="mr-1" />
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      {/* Properties Grid */}
      {loading ? (
        <div className="text-center py-20 text-white/60">Loading properties...</div>
      ) : filteredProperties.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="properties-grid">
          <AnimatePresence>
            {filteredProperties.map((property) => (
              <motion.div
                key={property.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="glass-effect border border-dark-lighter rounded-xl overflow-hidden group"
              >
                <div className="relative h-48">
                  <img
                    src={property.images[0]}
                    alt={property.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark to-transparent" />
                  
                  {/* Featured Badge */}
                  {property.featured && (
                    <div className="absolute top-3 left-3 bg-rose-gold text-dark px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full">
                      Featured
                    </div>
                  )}
                  
                  {/* Status Badge */}
                  <div className={`absolute top-3 right-3 px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full ${
                    property.status === 'Available' 
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                    {property.status}
                  </div>

                  {/* Quick Actions */}
                  <div className="absolute bottom-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => toggleFeatured(property)}
                      className={`p-2 rounded-full transition-all ${
                        property.featured 
                          ? 'bg-rose-gold text-dark' 
                          : 'bg-dark/80 text-white hover:bg-rose-gold hover:text-dark'
                      }`}
                      title={property.featured ? 'Remove from featured' : 'Add to featured'}
                    >
                      <Star size={16} fill={property.featured ? 'currentColor' : 'none'} />
                    </button>
                  </div>
                </div>

                <div className="p-5">
                  <div className="flex items-start gap-2 mb-2">
                    <Building2 size={16} className="text-rose-gold mt-1 flex-shrink-0" />
                    <span className="text-white/60 text-sm">{property.builder}</span>
                  </div>
                  
                  <h3 className="font-heading text-xl font-semibold text-white mb-2 line-clamp-1">
                    {property.title}
                  </h3>
                  
                  <div className="flex items-center gap-2 text-white/60 text-sm mb-3">
                    <MapPin size={14} />
                    <span className="line-clamp-1">{property.location}</span>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <p className="text-rose-gold font-heading text-2xl font-bold">
                      {formatPrice(property.price)}
                    </p>
                    <p className="text-white/40 text-sm">
                      {property.area} {property.area_unit}
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleEdit(property)}
                      data-testid={`edit-property-${property.id}`}
                      className="flex-1 bg-transparent border border-rose-gold text-rose-gold hover:bg-rose-gold hover:text-dark transition-all"
                    >
                      <Edit size={16} className="mr-1" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDelete(property.id)}
                      data-testid={`delete-property-${property.id}`}
                      className="flex-1 bg-transparent border border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-all"
                    >
                      <Trash2 size={16} className="mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <div className="text-center py-20">
          <Building2 className="mx-auto text-white/20 mb-4" size={64} />
          <p className="text-white/60 text-lg mb-4">
            {searchTerm || filterBuilder !== 'all' || filterStatus !== 'all'
              ? 'No properties match your filters'
              : 'No properties yet'}
          </p>
          <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="btn-primary">
            <Plus size={18} className="mr-2" />
            Add Your First Property
          </Button>
        </div>
      )}

      {/* Add/Edit Property Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-dark-light border-dark-lighter text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-rose-gold">
              {editingProperty ? 'Edit Property' : 'Add New Property'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="property-form">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-white/60 text-sm uppercase tracking-wider border-b border-dark-lighter pb-2">
                Basic Information
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">
                    Property Title *
                  </Label>
                  <Input
                    name="title"
                    data-testid="property-title-input"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Lodha World Towers"
                    className="bg-dark border-dark-lighter text-white"
                  />
                </div>
                <div>
                  <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">
                    Builder Name *
                  </Label>
                  <Input
                    name="builder"
                    data-testid="property-builder-input"
                    value={formData.builder}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Lodha Group"
                    className="bg-dark border-dark-lighter text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">
                    Property Type *
                  </Label>
                  <Select
                    value={formData.property_type}
                    onValueChange={(v) => handleSelectChange('property_type', v)}
                  >
                    <SelectTrigger className="bg-dark border-dark-lighter text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-dark-light border-dark-lighter">
                      <SelectItem value="Apartment" className="text-white">Apartment</SelectItem>
                      <SelectItem value="Villa" className="text-white">Villa</SelectItem>
                      <SelectItem value="Penthouse" className="text-white">Penthouse</SelectItem>
                      <SelectItem value="Duplex" className="text-white">Duplex</SelectItem>
                      <SelectItem value="Plot" className="text-white">Plot</SelectItem>
                      <SelectItem value="Commercial" className="text-white">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">
                    Price (₹) *
                  </Label>
                  <Input
                    name="price"
                    type="number"
                    data-testid="property-price-input"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    placeholder="e.g., 35000000"
                    className="bg-dark border-dark-lighter text-white"
                  />
                </div>
                <div>
                  <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">
                    Status *
                  </Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => handleSelectChange('status', v)}
                  >
                    <SelectTrigger className="bg-dark border-dark-lighter text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-dark-light border-dark-lighter">
                      <SelectItem value="Available" className="text-white">Available</SelectItem>
                      <SelectItem value="Sold" className="text-white">Sold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="space-y-4">
              <h3 className="text-white/60 text-sm uppercase tracking-wider border-b border-dark-lighter pb-2">
                Location
              </h3>
              
              <div>
                <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">
                  Address *
                </Label>
                <Input
                  name="location"
                  data-testid="property-location-input"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Lower Parel, Mumbai, Maharashtra"
                  className="bg-dark border-dark-lighter text-white"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">
                    Area *
                  </Label>
                  <Input
                    name="area"
                    type="number"
                    value={formData.area}
                    onChange={handleChange}
                    required
                    placeholder="e.g., 2200"
                    className="bg-dark border-dark-lighter text-white"
                  />
                </div>
                <div>
                  <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">
                    Unit
                  </Label>
                  <Select
                    value={formData.area_unit}
                    onValueChange={(v) => handleSelectChange('area_unit', v)}
                  >
                    <SelectTrigger className="bg-dark border-dark-lighter text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-dark-light border-dark-lighter">
                      <SelectItem value="sq ft" className="text-white">sq ft</SelectItem>
                      <SelectItem value="sq m" className="text-white">sq m</SelectItem>
                      <SelectItem value="sq yard" className="text-white">sq yard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleChange}
                      className="w-5 h-5 rounded border-dark-lighter bg-dark text-rose-gold focus:ring-rose-gold"
                    />
                    <span className="text-white">Feature on Homepage</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">
                    Latitude (optional)
                  </Label>
                  <Input
                    name="latitude"
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={handleChange}
                    placeholder="e.g., 19.0760"
                    className="bg-dark border-dark-lighter text-white"
                  />
                </div>
                <div>
                  <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">
                    Longitude (optional)
                  </Label>
                  <Input
                    name="longitude"
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={handleChange}
                    placeholder="e.g., 72.8777"
                    className="bg-dark border-dark-lighter text-white"
                  />
                </div>
              </div>
            </div>

            {/* Description & Details */}
            <div className="space-y-4">
              <h3 className="text-white/60 text-sm uppercase tracking-wider border-b border-dark-lighter pb-2">
                Description & Details
              </h3>

              <div>
                <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">
                  Description *
                </Label>
                <Textarea
                  name="description"
                  data-testid="property-description-input"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="Describe the property in detail..."
                  className="bg-dark border-dark-lighter text-white resize-none"
                />
              </div>

              <div>
                <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">
                  Key Highlights (one per line)
                </Label>
                <Textarea
                  name="highlights"
                  value={formData.highlights}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Sky-high swimming pool&#10;Private cinema&#10;Fitness center with spa"
                  className="bg-dark border-dark-lighter text-white resize-none"
                />
              </div>
            </div>

            {/* Images */}
            <div className="space-y-4">
              <h3 className="text-white/60 text-sm uppercase tracking-wider border-b border-dark-lighter pb-2">
                <ImagePlus size={16} className="inline mr-2" />
                Images
              </h3>

              <div>
                <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">
                  Image URLs * (one per line)
                </Label>
                <Textarea
                  name="images"
                  data-testid="property-images-input"
                  value={formData.images}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="https://images.unsplash.com/photo-xxx&#10;https://images.unsplash.com/photo-yyy"
                  className="bg-dark border-dark-lighter text-white resize-none font-mono text-sm"
                />
                <p className="text-white/40 text-xs mt-2">
                  Tip: Use high-quality images from Unsplash or your own hosted images
                </p>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 pt-4 border-t border-dark-lighter">
              <Button
                type="submit"
                data-testid="submit-property-button"
                disabled={saving}
                className="flex-1 btn-primary"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : editingProperty ? (
                  'Update Property'
                ) : (
                  'Create Property'
                )}
              </Button>
              <Button
                type="button"
                onClick={() => setIsDialogOpen(false)}
                className="px-8 bg-transparent border border-dark-lighter text-white/60 hover:text-white hover:border-white/30"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProperties;
